import z from 'zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FormSection from './components/FormSection';
import FormFieldLabel from './components/FormFieldLabel';
import FormTextField from './components/FormTextField';
import { useDispatch, useSelector } from 'react-redux';
import { getUserState } from '../../features/user/selectors';
import { IUser, ProjectProgressReportData } from '../../api-client/autogenerated';
import IconButton from '@material-ui/core/IconButton';
import { AddCircleOutlined, RemoveCircleOutlined, Save } from '@material-ui/icons';
import FormDatePicker from './components/FormDatePicker';
import { addSnackbar } from '../../features/snackbar/actions';
import {
  editProjectProgressReportFormById,
  insertProjectProgressReportForm,
} from '../../models/api/project-lifecycle';
import { getProjectState } from '../../features/project/selectors';
import Button from '@material-ui/core/Button';
import CircularLoader from '../loader/CircularLoader';
import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import { reloadProject } from '../../features/project/actions';
import { useMediaQuery } from '@material-ui/core';
import dayjs from 'dayjs';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles(() =>
  createStyles({
    printBreakBefore: {
      pageBreakBefore: 'always',
      '@media print': {
        paddingTop: 16,
      },
    },
    hideOnPrint: {
      '@media print': {
        display: 'none',
      },
    },
    printShort: {
      '@media print': {
        width: '60%',
      },
    },
  }),
);

const schema = z.object({
  reportDate: z.string(),

  projectNumber: z.string().optional(),
  projectName: z.string().optional(),
  submittedByName: z.string(),
  submittedByCompany: z.string(),
  submittedTo: z.string().optional(),

  preDesignProgress: z.coerce.number().optional(),
  preDesignNotes: z.string().optional(),
  schematicDesignProgress: z.coerce.number().optional(),
  schematicDesignNotes: z.string().optional(),
  designDevelopmentProgress: z.coerce.number().optional(),
  designDevelopmentNotes: z.string().optional(),
  constructionDocumentsProgress: z.coerce.number().optional(),
  constructionDocumentsNotes: z.string().optional(),
  constructionAdministrationProgress: z.coerce.number().optional(),
  constructionAdministrationNotes: z.string().optional(),

  accomplishments: z
    .array(
      z.object({
        value: z.string(),
      }),
    )
    .optional(),
  upcomingActivities: z
    .array(
      z.object({
        value: z.string(),
      }),
    )
    .optional(),
  decisions: z
    .array(
      z.object({
        value: z.string(),
      }),
    )
    .optional(),
  targetDates: z
    .array(
      z.object({
        value: z.string(),
      }),
    )
    .optional(),

  invoiceStatus: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const getDefaultValues = (user?: IUser): FormValues => {
  return {
    reportDate: dayjs().format('MM/DD/YYYY'),

    projectNumber: '',
    projectName: '',
    submittedByName: user?.name || '',
    submittedByCompany: user?.company?.name || '',
    submittedTo: '',

    preDesignProgress: 0,
    preDesignNotes: '',
    schematicDesignProgress: 0,
    schematicDesignNotes: '',
    designDevelopmentProgress: 0,
    designDevelopmentNotes: '',
    constructionDocumentsProgress: 0,
    constructionDocumentsNotes: '',
    constructionAdministrationProgress: 0,
    constructionAdministrationNotes: '',

    accomplishments: [{ value: '' }],
    upcomingActivities: [{ value: '' }],
    decisions: [{ value: '' }],
    targetDates: [{ value: '' }],

    invoiceStatus: '',
  };
};

type SchemaInput = z.input<typeof schema>;
type SchemaOutput = z.output<typeof schema>;

const parseData = (data: ProjectProgressReportData): SchemaInput => {
  return {
    ...data,
    accomplishments: data.accomplishments?.map((x) => ({ value: x })) || [],
    upcomingActivities: data.upcomingActivities?.map((x) => ({ value: x })) || [],
    decisions: data.decisions?.map((x) => ({ value: x })) || [],
    targetDates: data.targetDates?.map((x) => ({ value: x })) || [],
  };
};

interface Props {
  reportId?: string | null;
  canEdit: boolean;
  isEditing: boolean;
  publicData?: ProjectProgressReportData | null;
}

export default function ProjectProgressReportForm(props: Props) {
  const { canEdit, isEditing, reportId, publicData } = props;
  const classes = useStyles();

  const dispatch = useDispatch();
  const project = useSelector(getProjectState);
  const user = useSelector(getUserState);

  const isMobile = useMediaQuery('(max-width: 800px)');

  const progressReport = useMemo(
    () => project?.projectProgressReports?.find((x) => x.id === reportId),
    [project, reportId],
  );

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isSubmitting, dirtyFields },
  } = useForm({
    disabled: !canEdit,
    resolver: zodResolver(schema),
    defaultValues: !progressReport && !publicData ? getDefaultValues(user) : undefined,
  });

  const fillFormFieldsFromProject = () => {
    if (!project) return;

    if (project.architectProjectNumber) {
      setValue('projectNumber', project.architectProjectNumber);
    }
    setValue('projectName', project.name);
  };

  useEffect(() => {
    if (publicData) {
      reset(parseData(publicData));
    } else if (progressReport) {
      if (progressReport?.data) {
        reset(parseData(progressReport.data));
      }
    } else {
      fillFormFieldsFromProject();
    }
  }, [progressReport, publicData]);

  useEffect(() => {
    if (!_.isEmpty(dirtyFields)) {
      dispatch(blockNavigation());
    } else {
      dispatch(allowNavigation());
    }
  }, [dirtyFields]);

  const {
    fields: accomplishments,
    append: appendAccomplishment,
    remove: removeAccomplishment,
  } = useFieldArray({
    control,
    name: 'accomplishments',
  });

  const {
    fields: upcomingActivities,
    append: appendUpcomingActivity,
    remove: removeUpcomingActivity,
  } = useFieldArray({
    control,
    name: 'upcomingActivities',
  });

  const { fields: decisions, append: appendDecision, remove: removeDecision } = useFieldArray({
    control,
    name: 'decisions',
  });

  const { fields: targetDates, append: appendTargetDate, remove: removeTargetDate } = useFieldArray(
    {
      control,
      name: 'targetDates',
    },
  );

  const onSubmit = handleSubmit(
    async (data) => {
      await submit(data);

      reset(undefined, { keepValues: true });

      dispatch(allowNavigation());

      dispatch(
        addSnackbar({ id: Date.now(), message: 'Form saved successfully', severity: 'success' }),
      );
      return;
    },
    (errors) => {
      console.log('errors', errors);
    },
  );

  const submit = async (data: FormValues) => {
    if (!project) return;

    const body = {
      ...data,
      accomplishments: data.accomplishments?.map((x) => x.value).filter((x) => !!x),
      upcomingActivities: data.upcomingActivities?.map((x) => x.value).filter((x) => !!x),
      decisions: data.decisions?.map((x) => x.value).filter((x) => !!x),
      targetDates: data.targetDates?.map((x) => x.value).filter((x) => !!x),
    };

    if (!reportId) {
      await insertProjectProgressReportForm(project.id, body);
    } else {
      await editProjectProgressReportFormById(project.id, reportId, body);
    }

    window.scrollTo({ behavior: 'smooth', top: 0, left: 0 });
    dispatch(reloadProject());
  };

  return (
    <form noValidate onSubmit={onSubmit} style={{ paddingBottom: isEditing ? 0 : 16 }}>
      <FormSection>
        <FormFieldLabel>Report Date:</FormFieldLabel>
        <FormDatePicker name="reportDate" control={control} className={classes.printShort} />
      </FormSection>
      <FormSection title="Details">
        <FormFieldLabel>Grace Design Studios Project Number:</FormFieldLabel>
        <FormTextField name="projectNumber" control={control} />

        <FormFieldLabel>Grace Design Studios Project Name:</FormFieldLabel>
        <FormTextField name="projectName" control={control} />

        <FormFieldLabel>Submitted by:</FormFieldLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FormTextField name="submittedByName" control={control} fullWidth disabled />
          <FormTextField name="submittedByCompany" control={control} fullWidth disabled />
        </div>

        <FormFieldLabel>Submitted to:</FormFieldLabel>
        <FormTextField name="submittedTo" control={control} />
      </FormSection>

      <FormSection
        title="Percentage of work complete"
        formGridProps={{ gridTemplateColumns: '200px 80px 1fr' }}
      >
        <FormFieldLabel>Pre-Design:</FormFieldLabel>
        <FormTextField name="preDesignProgress" control={control} signedNumericOnly suffix="%" />
        <FormTextField name="preDesignNotes" control={control} />

        <FormFieldLabel>Schematic Design:</FormFieldLabel>
        <FormTextField
          name="schematicDesignProgress"
          control={control}
          signedNumericOnly
          suffix="%"
        />
        <FormTextField name="schematicDesignNotes" control={control} />

        <FormFieldLabel>Design Development:</FormFieldLabel>
        <FormTextField
          name="designDevelopmentProgress"
          control={control}
          signedNumericOnly
          suffix="%"
        />
        <FormTextField name="designDevelopmentNotes" control={control} />

        <FormFieldLabel>Construction Documents:</FormFieldLabel>
        <FormTextField
          name="constructionDocumentsProgress"
          control={control}
          signedNumericOnly
          suffix="%"
        />
        <FormTextField name="constructionDocumentsNotes" control={control} />

        <FormFieldLabel>Construction Administration:</FormFieldLabel>
        <FormTextField
          name="constructionAdministrationProgress"
          control={control}
          signedNumericOnly
          suffix="%"
        />
        <FormTextField name="constructionAdministrationNotes" control={control} />
      </FormSection>

      <FormSection
        title="Accomplishments this month"
        noGrid
        className={classes.printBreakBefore}
        style={{ justifyContent: 'flex-start' }}
      >
        {!isEditing && accomplishments.length === 0 ? (
          <Typography style={{ marginLeft: 1 }}>None.</Typography>
        ) : null}
        {accomplishments.map((field, index) => (
          <div
            key={field.id}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10 }}
          >
            <FormTextField
              name={`accomplishments.${index}.value`}
              control={control}
              style={{ width: '100%' }}
            />
            {canEdit ? (
              <IconButton
                onClick={() => removeAccomplishment(index)}
                style={{ width: 'min-content', padding: 0 }}
              >
                <RemoveCircleOutlined />
              </IconButton>
            ) : null}
          </div>
        ))}
        {canEdit ? (
          <IconButton
            onClick={() => appendAccomplishment({ value: '' })}
            style={{ width: 'min-content', padding: 4 }}
          >
            <AddCircleOutlined />
          </IconButton>
        ) : null}
      </FormSection>

      <FormSection title="Upcoming Activities" noGrid style={{ justifyContent: 'flex-start' }}>
        {!isEditing && upcomingActivities.length === 0 ? (
          <Typography style={{ marginLeft: 1 }}>None.</Typography>
        ) : null}
        {upcomingActivities.map((field, index) => (
          <div
            key={field.id}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10 }}
          >
            <FormTextField
              name={`upcomingActivities.${index}.value`}
              control={control}
              style={{ width: '100%' }}
            />
            {canEdit ? (
              <IconButton
                onClick={() => removeUpcomingActivity(index)}
                style={{ width: 'min-content', padding: 0 }}
              >
                <RemoveCircleOutlined />
              </IconButton>
            ) : null}
          </div>
        ))}
        {canEdit ? (
          <IconButton
            onClick={() => appendUpcomingActivity({ value: '' })}
            style={{ width: 'min-content', padding: 4 }}
          >
            <AddCircleOutlined />
          </IconButton>
        ) : null}
      </FormSection>

      <FormSection title="Decisions Needed" noGrid style={{ justifyContent: 'flex-start' }}>
        {!isEditing && decisions.length === 0 ? (
          <Typography style={{ marginLeft: 1 }}>None.</Typography>
        ) : null}
        {decisions.map((field, index) => (
          <div
            key={field.id}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10 }}
          >
            <FormTextField
              name={`decisions.${index}.value`}
              control={control}
              style={{ width: '100%' }}
            />
            {canEdit ? (
              <IconButton
                onClick={() => removeDecision(index)}
                style={{ width: 'min-content', padding: 0 }}
              >
                <RemoveCircleOutlined />
              </IconButton>
            ) : null}
          </div>
        ))}
        {canEdit ? (
          <IconButton
            onClick={() => appendDecision({ value: '' })}
            style={{ width: 'min-content', padding: 4 }}
          >
            <AddCircleOutlined />
          </IconButton>
        ) : null}
      </FormSection>

      <FormSection title="Target Dates" noGrid style={{ justifyContent: 'flex-start' }}>
        {!isEditing && targetDates.length === 0 ? (
          <Typography style={{ marginLeft: 1 }}>None.</Typography>
        ) : null}
        {targetDates.map((field, index) => (
          <div
            key={field.id}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10 }}
          >
            <FormTextField
              name={`targetDates.${index}.value`}
              control={control}
              style={{ width: '100%' }}
            />
            {canEdit ? (
              <IconButton
                onClick={() => removeTargetDate(index)}
                style={{ width: 'min-content', padding: 0 }}
              >
                <RemoveCircleOutlined />
              </IconButton>
            ) : null}
          </div>
        ))}
        {canEdit ? (
          <IconButton
            onClick={() => appendTargetDate({ value: '' })}
            style={{ width: 'min-content', padding: 4 }}
          >
            <AddCircleOutlined />
          </IconButton>
        ) : null}
      </FormSection>

      <FormSection title="Invoice Status" noGrid style={{ pageBreakInside: 'avoid' }}>
        <FormTextField
          name="invoiceStatus"
          control={control}
          large
          style={{
            borderBottom: '1px solid #BCBCBC',
            borderBottomRightRadius: 4,
            borderBottomLeftRadius: 4,
          }}
        />
      </FormSection>

      {isEditing ? (
        <div
          className={classes.hideOnPrint}
          style={{
            position: isMobile ? 'static' : 'sticky',
            bottom: 0,
            width: 'calc(100% + 32px)',
            padding: 16,
            marginLeft: -16,
            backgroundColor: '#FFF',
            borderTop: '1px solid #CCC',
            zIndex: 100,
          }}
        >
          {!isSubmitting ? (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!canEdit}
              startIcon={<Save />}
            >
              Save Changes
            </Button>
          ) : (
            <CircularLoader style={{ justifyContent: 'start', paddingLeft: 32 }} />
          )}
        </div>
      ) : null}
    </form>
  );
}
